/**
 * ToastContext.js — global toast notifications.
 *
 * Mount <ToastProvider> at the app root; any descendant can call
 *   const { showToast } = useToast();
 *   showToast('Couldn\'t save. Try again.', 'error');
 *
 * Types: 'success' | 'error' | 'info' | 'assignment'
 * Default duration: 3500ms (errors 6000ms, assignment 5000ms — important to not miss)
 * Pass options.title for a bold heading above the message (used by 'assignment').
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X, Bell } from 'lucide-react';

const ToastContext = createContext(null);

const TYPE_STYLES = {
  success: { color: '#22c55e', bg: 'rgba(34,197,94,0.10)', border: 'rgba(34,197,94,0.35)', Icon: CheckCircle2 },
  error:   { color: '#f87171', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.40)', Icon: AlertCircle },
  info:    { color: '#38bdf8', bg: 'rgba(56,189,248,0.10)', border: 'rgba(56,189,248,0.35)', Icon: Info },
  // Richer "push notification" style banner (bold title + message), used for
  // things like a live issue-assignment alert — visually distinct from the
  // plain info/success/error toasts above.
  assignment: { color: '#18bfb5', bg: 'rgba(24,191,181,0.12)', border: 'rgba(24,191,181,0.4)', Icon: Bell },
};

let _id = 0;
const nextId = () => ++_id;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', options = {}) => {
    const id = nextId();
    const duration = options.duration ?? (type === 'error' ? 6000 : type === 'assignment' ? 5000 : 3500);
    setToasts((prev) => [...prev, { id, type, message, title: options.title || null, action: options.action || null }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

const ToastViewport = ({ toasts, onDismiss }) => (
  <div
    aria-live="polite"
    aria-atomic="true"
    style={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 100000,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      maxWidth: 'min(92vw, 380px)',
      pointerEvents: 'none',
    }}
  >
    {toasts.map((t) => {
      const style = TYPE_STYLES[t.type] || TYPE_STYLES.info;
      const { Icon } = style;
      return (
        <div
          key={t.id}
          role="alert"
          style={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 10,
            background: 'var(--card-bg-solid, #111820)',
            border: `1px solid ${style.border}`,
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            color: 'var(--text-primary, #e2e8f0)',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <div style={{
            flexShrink: 0,
            width: 28, height: 28, borderRadius: 8,
            background: style.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={16} style={{ color: style.color }} />
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
            {t.title && (
              <div style={{ fontWeight: 700, marginBottom: 2, wordBreak: 'break-word' }}>{t.title}</div>
            )}
            <div style={{ wordBreak: 'break-word' }}>{t.message}</div>
            {t.action && (
              <button
                type="button"
                onClick={() => { try { t.action.onClick?.(); } finally { onDismiss(t.id); } }}
                style={{
                  marginTop: 6,
                  background: 'transparent',
                  color: style.color,
                  border: 'none',
                  padding: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notification"
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted, #94a3b8)',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              lineHeight: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>
      );
    })}
  </div>
);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe no-op if a component renders outside the provider; logs to console
    // so the missing wiring is visible in dev.
    return {
      showToast: (message, type = 'info') => {
        // eslint-disable-next-line no-console
        console[type === 'error' ? 'error' : 'log']('[toast:noop]', type, message);
      },
      dismissToast: () => {},
    };
  }
  return ctx;
};

export default ToastProvider;
